import { useEffect, useState } from "react";
import { Tag, CheckCircle, XCircle } from "@phosphor-icons/react";
import API from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/* Reseller discount code (e.g. PETE10) with a live validity check — shared by sign-up, billing and tenant creation. */
export function ResellerCodeField({ value, onChange, onValidated, label = "Reseller / discount code", hint = "Optional — got a code from a Fixlr partner? Enter it for their discount.", testId = "reseller-code" }) {
  const [state, setState] = useState(null); // null | checking | {valid, discount_pct, reseller_name}
  const code = (value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  useEffect(() => {
    if (!code) { setState(null); onValidated?.(null); return; }
    let live = true;
    setState("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/public/reseller-codes/${encodeURIComponent(code)}`);
        if (!live) return;
        setState(data);
        onValidated?.(data?.valid ? data : null);
      } catch {
        if (!live) return;
        setState({ valid: false });
        onValidated?.(null);
      }
    }, 400);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div data-testid={`${testId}-field`}>
      <Label htmlFor={`${testId}-input`} className="text-xs flex items-center gap-1"><Tag size={12} /> {label}</Label>
      <Input id={`${testId}-input`} value={value || ""} onChange={(e) => onChange(e.target.value.toUpperCase())} placeholder="e.g. PETE10" className="mt-1 uppercase tracking-wider" maxLength={18} autoComplete="off" data-testid={`${testId}-input`} />
      {!code && <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>}
      {code && state === "checking" && <p className="text-[10px] text-muted-foreground mt-1" data-testid={`${testId}-checking`}>Checking…</p>}
      {code && state && state !== "checking" && state.valid && (
        <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1" data-testid={`${testId}-valid`}>
          <CheckCircle size={13} weight="fill" /> {state.discount_pct}% off your subscription for as long as you stay — via {state.reseller_name}
        </p>
      )}
      {code && state && state !== "checking" && !state.valid && (
        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid={`${testId}-invalid`}><XCircle size={13} weight="fill" /> That code isn't active — check it or leave the field blank</p>
      )}
    </div>
  );
}
